// utils
import {lazy, Suspense} from 'react';

// components
import RouteChangeProgress from '@ui/RouteChangeProgress';
import Panel from '@layout/Panel';
import Sidebar from '@layout/Sidebar';
import {Navigate, Route, Routes, useLocation, useNavigate} from 'react-router-dom';
import BottomMenu from '@layout/BottomMenu';
import AppModuleTour from '@components/AppModuleTour';
import WidgetsLoader from '@components/WidgetsLoader';
import NotificationManager from '@components/NotificationManager';
import {authService} from '@services/auth.service.js';
import {isStandardUserRole, isSuperAdmin} from '@utils/roleAccess';
import {requiresClinicSelection} from '@utils/tenant';
import {ClientPortalRoutes, isClientPortalPath} from './apps/client-portal/routes';
import PostLoginNavigate from './apps/client-portal/guards/PostLoginNavigate';

// hooks
import useWindowSize from '@hooks/useWindowSize';
import {useAuth} from '@hooks/useAuth';
import {useRef, useEffect} from 'react';
import {useSidebarContext} from '@contexts/sidebarContext';

// pages
const DashboardA = lazy(() => import('@pages/DashboardA'));
const DoctorAppointments = lazy(() => import('@pages/DoctorAppointments'));
const AgendaPropuestas = lazy(() => import('@pages/AgendaPropuestas'));
const PatientAppointments = lazy(() => import('@pages/PatientAppointments'));
const Patients = lazy(() => import('@pages/Patients'));
const RegisterPatient = lazy(() => import('@pages/RegisterPatient'));
const ClinicalHistory = lazy(() => import('@pages/ClinicalHistory'));
const RegisterClinicalHistory = lazy(() => import('@pages/RegisterClinicalHistory'));
const MedicinaTrabajo = lazy(() => import('@pages/MedicinaTrabajo'));
const MedicinaTrabajoHistoriaForm = lazy(() => import('@pages/MedicinaTrabajoHistoriaForm'));
const MedicinaTrabajoCertificadoAptitudForm = lazy(() => import('@pages/MedicinaTrabajoCertificadoAptitudForm'));
const MedicinaTrabajoCertificadoEvaluacionForm = lazy(() => import('@pages/MedicinaTrabajoCertificadoEvaluacionForm'));
const Inventory = lazy(() => import('@pages/Inventory'));
const RegisterInventoryProduct = lazy(() => import('@pages/RegisterInventoryProduct'));
const MovementsIncomes = lazy(() => import('@pages/MovementsIncomes'));
const MovementsExpenses = lazy(() => import('@pages/MovementsExpenses'));
const SettingsMovementTypes = lazy(() => import('@pages/SettingsMovementTypes'));
const RegisterMovementType = lazy(() => import('@pages/RegisterMovementType'));
const RegisterMovementIncome = lazy(() => import('@pages/RegisterMovementIncome'));
const RegisterMovementExpense = lazy(() => import('@pages/RegisterMovementExpense'));
const ViewMovementIncome = lazy(() => import('@pages/ViewMovementIncome'));
const EditMovementIncome = lazy(() => import('@pages/EditMovementIncome'));
const ViewMovementExpense = lazy(() => import('@pages/ViewMovementExpense'));
const EditMovementExpense = lazy(() => import('@pages/EditMovementExpense'));
const Receivables = lazy(() => import('@pages/Receivables'));
const RegisterReceivable = lazy(() => import('@pages/RegisterReceivable'));
const ViewReceivable = lazy(() => import('@pages/ViewReceivable'));
const InventoryPurchases = lazy(() => import('@pages/InventoryPurchases'));
const RegisterInventoryPurchase = lazy(() => import('@pages/RegisterInventoryPurchase'));
const Tests = lazy(() => import('@pages/Tests'));
const Studies = lazy(() => import('@pages/Studies'));
const RegisterStudy = lazy(() => import('@pages/RegisterStudy'));
const Doctors = lazy(() => import('@pages/Doctors'));
const StaffList = lazy(() => import('@pages/Staff'));
const DoctorMessenger = lazy(() => import('@pages/DoctorMessenger'));
const PatientMessenger = lazy(() => import('@pages/PatientMessenger'));
const DoctorsReviews = lazy(() => import('@pages/DoctorsReviews'));
const PatientReviews = lazy(() => import('@pages/PatientReviews'));
const Finances = lazy(() => import('@pages/Finances'));
const SettingsProfile = lazy(() => import('@pages/SettingsProfile'));
const SettingsProfiles = lazy(() => import('@pages/SettingsProfiles'));
const SettingsProfileDetail = lazy(() => import('@pages/SettingsProfileDetail'));
const SettingsInventoryTypes = lazy(() => import('@pages/SettingsInventoryTypes'));
const RegisterInventoryType = lazy(() => import('@pages/RegisterInventoryType'));
const SettingsCommitmentTypes = lazy(() => import('@pages/SettingsCommitmentTypes'));
const RegisterCommitmentType = lazy(() => import('@pages/RegisterCommitmentType'));
const RegisterAgenda = lazy(() => import('@pages/RegisterAgenda'));
const CRMBoard = lazy(() => import('@pages/CRMBoard'));
const SettingsCompanies = lazy(() => import('@pages/SettingsCompanies'));
const RegisterCompany = lazy(() => import('@pages/RegisterCompany'));
const PortalEmpresasAccess = lazy(() => import('@pages/PortalEmpresasAccess'));
const SettingsSuppliers = lazy(() => import('@pages/SettingsSuppliers'));
const RegisterSupplier = lazy(() => import('@pages/RegisterSupplier'));
const SettingsDisplay = lazy(() => import('@pages/SettingsDisplay'));
const SettingsFormularios = lazy(() => import('@pages/SettingsFormularios'));
const PageNotFound = lazy(() => import('@pages/PageNotFound'));
const Login = lazy(() => import('@pages/Login'));
const Register = lazy(() => import('@pages/Register'));
const RegisterComplete = lazy(() => import('@pages/RegisterComplete'));
const ForgotPassword = lazy(() => import('@pages/ForgotPassword'));
const BulkEmployeeUpload = lazy(() => import('@pages/BulkEmployeeUpload'));
const ConfirmEmail = lazy(() => import('@pages/ConfirmEmail'));
const ResetPassword = lazy(() => import('@pages/ResetPassword'));
const AccessPending = lazy(() => import('@pages/AccessPending'));
const SelectClinic = lazy(() => import('@pages/SelectClinic'));
const RegisterClinic = lazy(() => import('@pages/RegisterClinic'));
const Landing = lazy(() => import('@pages/Landing'));
const Presentacion = lazy(() => import('@pages/Presentacion'));
const AdminRoles = lazy(() => import('@pages/AdminRoles'));

const SuperAdminRoute = ({children}) => {
    if (!authService.isAuthenticated()) {
        return <Navigate to="/login" replace />;
    }

    if (authService.isImpersonationViewActive()) {
        return <Navigate to="/portal-cliente/dashboard" replace />;
    }

    const currentUser = authService.getUser();

    if (!isSuperAdmin(currentUser)) {
        return <Navigate to="/dashboard_a" replace />;
    }

    return children;
};

const ProtectedRoute = ({children}) => {
    const {pathname} = useLocation();

    if (!authService.isAuthenticated()) {
        return <Navigate to="/login" replace />;
    }

    if (authService.isImpersonationViewActive()) {
        return <Navigate to="/portal-cliente/dashboard" replace />;
    }

    const currentUser = authService.getUser();
    const isAccessPendingRoute = pathname === '/access-pending';
    const isSelectClinicRoute = pathname === '/select-clinic';

    if (isStandardUserRole(currentUser) && !isAccessPendingRoute && !isSelectClinicRoute) {
        return <Navigate to="/access-pending" replace />;
    }

    if (authService.isAdminEmpresaCliente(currentUser)) {
        return <PostLoginNavigate />;
    }

    if (requiresClinicSelection(currentUser) && !isSelectClinicRoute && !isAccessPendingRoute) {
        if (authService.getAvailableClinics().length > 0) {
            return <Navigate to="/select-clinic" replace />;
        }

        return <Navigate to="/access-pending" replace />;
    }

    return children;
}

const GuestRoute = ({children}) => {
    if (!authService.isAuthenticated()) {
        return children;
    }

    if (authService.isImpersonationViewActive()) {
        return <Navigate to="/portal-cliente/dashboard" replace />;
    }

    const currentUser = authService.getUser();

    if (isStandardUserRole(currentUser)) {
        return <Navigate to="/access-pending" replace />;
    }

    if (authService.isAdminEmpresaCliente(currentUser)) {
        return <PostLoginNavigate />;
    }

    if (requiresClinicSelection(currentUser)) {
        return <Navigate to="/select-clinic" replace />;
    }

    return <Navigate to="/dashboard_a" replace />;
}

const TENANT_GUARD_EXEMPT_PATHS = new Set([
    '/login',
    '/register',
    '/register-complete',
    '/forgot-password',
    '/confirm-email',
    '/reset-password',
    '/access-pending',
    '/select-clinic',
    '/register-clinic',
    '/presentacion',
    '/admin/roles',
    '/404'
]);

const AppLayout = () => {
    const appRef = useRef(null);
    const navigate = useNavigate();
    const {width} = useWindowSize();
    const {isCollapsed} = useSidebarContext();
    const {isAuthenticated, tenantId, isHydrating} = useAuth();
    const isMobile = width < 768;
    const {pathname} = useLocation();
    const isAuthRoute = ['/login', '/register', '/register-complete', '/forgot-password', '/confirm-email', '/reset-password', '/access-pending', '/select-clinic', '/register-clinic'].includes(pathname);
    const isPublicLanding = pathname === '/' && !authService.isAuthenticated();
    const isPresentation = pathname === '/presentacion';
    const isMinimalPublicShell = isAuthRoute || isPublicLanding || isPresentation;

    useEffect(() => {
        if (appRef.current) {
            appRef.current.scrollTo(0, 0);
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated || isHydrating) {
            return;
        }

        if (TENANT_GUARD_EXEMPT_PATHS.has(pathname) || isClientPortalPath(pathname)) {
            return;
        }

        const currentUser = authService.getUser();

        if (isStandardUserRole(currentUser)) {
            return;
        }

        if (authService.isImpersonationViewActive()) {
            navigate('/portal-cliente/dashboard', {replace: true});
            return;
        }

        if (authService.isAdminEmpresaCliente(currentUser)) {
            return;
        }

        if (!tenantId) {
            navigate('/select-clinic', {replace: true});
        }
    }, [isAuthenticated, isHydrating, tenantId, pathname, navigate]);

    // Landing y autenticación sin layout del dashboard
    if (isMinimalPublicShell) {
        return (
            <Suspense fallback={<WidgetsLoader />}>
                <Routes>
                    <Route path="/" element={<Landing/>}/>
                    <Route path="/presentacion" element={<Presentacion/>}/>
                    <Route path="/login" element={<GuestRoute><Login/></GuestRoute>}/>
                    <Route path="/register" element={<GuestRoute><Register/></GuestRoute>}/>
                    <Route path="/register-complete" element={<GuestRoute><RegisterComplete/></GuestRoute>}/>
                    <Route path="/forgot-password" element={<GuestRoute><ForgotPassword/></GuestRoute>}/>
                    <Route path="/confirm-email" element={<ConfirmEmail/>}/>
                    <Route path="/reset-password" element={<ResetPassword/>}/>
                    <Route path="/access-pending" element={<ProtectedRoute><AccessPending/></ProtectedRoute>}/>
                    <Route path="/select-clinic" element={<SelectClinic/>}/>
                    <Route path="/register-clinic" element={<RegisterClinic/>}/>
                </Routes>
            </Suspense>
        )
    }

    return (
        <div className={`app ${isCollapsed ? 'sidebar-collapsed' : ''}`} ref={appRef}>
            <RouteChangeProgress/>
            <Sidebar/>
            <div className="app_content">
                <Panel/>
                <Suspense fallback={<WidgetsLoader />}>
                    <Routes key={tenantId ?? 'no-tenant'}>
                        <Route path="/" element={
                            authService.isAuthenticated()
                                ? <Navigate to="/dashboard_a" replace/>
                                : <Navigate to="/login" replace/>
                        }/>
                        <Route path="/dashboard_a" element={<ProtectedRoute><DashboardA/></ProtectedRoute>}/>
                        <Route path="/dashboard_b" element={<Navigate to="/dashboard_a" replace/>}/>
                        <Route path="/dashboard_c" element={<Navigate to="/dashboard_a" replace/>}/>
                        <Route path="/dashboard_d" element={<Navigate to="/dashboard_a" replace/>}/>
                        <Route path="/dashboard_e" element={<Navigate to="/dashboard_a" replace/>}/>
                        <Route path="/dashboard_f" element={<Navigate to="/dashboard_a" replace/>}/>
                        <Route path="/dashboard_g" element={<Navigate to="/dashboard_a" replace/>}/>
                        <Route path="/dashboard_h" element={<Navigate to="/dashboard_a" replace/>}/>
                        <Route path="/dashboard_i" element={<Navigate to="/dashboard_a" replace/>}/>
                        <Route path="/dashboard_j" element={<Navigate to="/dashboard_a" replace/>}/>
                        <Route path="/dashboard_k" element={<Navigate to="/dashboard_a" replace/>}/>
                        <Route path="/agenda" element={<ProtectedRoute><DoctorAppointments/></ProtectedRoute>}/>
                        <Route path="/agenda/propuestas" element={<ProtectedRoute><AgendaPropuestas/></ProtectedRoute>}/>
                        <Route path="/agenda/register" element={<ProtectedRoute><RegisterAgenda/></ProtectedRoute>}/>
                        <Route path="/doctor_appointments" element={<ProtectedRoute><DoctorAppointments/></ProtectedRoute>}/>
                        <Route path="/patient_appointments" element={<ProtectedRoute><PatientAppointments/></ProtectedRoute>}/>
                        <Route path="/patients" element={<ProtectedRoute><Patients/></ProtectedRoute>}/>
                        <Route path="/register-patient" element={<ProtectedRoute><RegisterPatient/></ProtectedRoute>}/>
                        <Route path="/historia-clinica" element={<ProtectedRoute><ClinicalHistory/></ProtectedRoute>}/>
                        <Route path="/historia-clinica/registrar" element={<ProtectedRoute><RegisterClinicalHistory/></ProtectedRoute>}/>
                        <Route path="/medicina-trabajo" element={<ProtectedRoute><MedicinaTrabajo/></ProtectedRoute>}/>
                        <Route path="/medicina-trabajo/historia" element={<ProtectedRoute><MedicinaTrabajoHistoriaForm/></ProtectedRoute>}/>
                        <Route path="/medicina-trabajo/certificado-aptitud" element={<ProtectedRoute><MedicinaTrabajoCertificadoAptitudForm/></ProtectedRoute>}/>
                        <Route path="/medicina-trabajo/certificado-evaluacion" element={<ProtectedRoute><MedicinaTrabajoCertificadoEvaluacionForm/></ProtectedRoute>}/>
                        <Route path="/inventory" element={<ProtectedRoute><Inventory/></ProtectedRoute>}/>
                        <Route path="/inventory/suppliers" element={<ProtectedRoute><SettingsSuppliers/></ProtectedRoute>}/>
                        <Route path="/inventory/suppliers/register" element={<ProtectedRoute><RegisterSupplier/></ProtectedRoute>}/>
                        <Route path="/inventory/purchases" element={<ProtectedRoute><InventoryPurchases/></ProtectedRoute>}/>
                        <Route path="/inventory/purchases/register" element={<ProtectedRoute><RegisterInventoryPurchase/></ProtectedRoute>}/>
                        <Route path="/movements/inventory-purchases" element={<Navigate to="/inventory/purchases" replace/>}/>
                        <Route path="/movements/inventory-purchases/register" element={<Navigate to="/inventory/purchases/register" replace/>}/>
                        <Route path="/movements/incomes" element={<ProtectedRoute><MovementsIncomes/></ProtectedRoute>}/>
                        <Route path="/movements/expenses" element={<ProtectedRoute><MovementsExpenses/></ProtectedRoute>}/>
                        <Route path="/movements/types" element={<ProtectedRoute><SettingsMovementTypes/></ProtectedRoute>}/>
                        <Route path="/movements/types/register" element={<ProtectedRoute><RegisterMovementType/></ProtectedRoute>}/>
                        <Route path="/movements/incomes/register" element={<ProtectedRoute><RegisterMovementIncome/></ProtectedRoute>}/>
                        <Route path="/movements/expenses/register" element={<ProtectedRoute><RegisterMovementExpense/></ProtectedRoute>}/>
                        <Route path="/movements/incomes/:id" element={<ProtectedRoute><ViewMovementIncome/></ProtectedRoute>}/>
                        <Route path="/movements/incomes/:id/edit" element={<ProtectedRoute><EditMovementIncome/></ProtectedRoute>}/>
                        <Route path="/movements/expenses/:id" element={<ProtectedRoute><ViewMovementExpense/></ProtectedRoute>}/>
                        <Route path="/movements/expenses/:id/edit" element={<ProtectedRoute><EditMovementExpense/></ProtectedRoute>}/>
                        <Route path="/movements/receivables" element={<ProtectedRoute><Receivables/></ProtectedRoute>}/>
                        <Route path="/movements/receivables/register" element={<ProtectedRoute><RegisterReceivable/></ProtectedRoute>}/>
                        <Route path="/movements/receivables/:id" element={<ProtectedRoute><ViewReceivable/></ProtectedRoute>}/>
                        <Route path="/crm" element={<ProtectedRoute><CRMBoard/></ProtectedRoute>}/>
                        <Route path="/crm/companies" element={<ProtectedRoute><SettingsCompanies/></ProtectedRoute>}/>
                        <Route path="/crm/companies/register" element={<ProtectedRoute><RegisterCompany/></ProtectedRoute>}/>
                        <Route path="/settings/portal-empresas" element={<ProtectedRoute><PortalEmpresasAccess/></ProtectedRoute>}/>
                        <Route path="/crm/companies/registro-completo" element={<Navigate to="/settings/portal-empresas" replace/>}/>
                        <Route path="/register-inventory-product" element={<ProtectedRoute><RegisterInventoryProduct/></ProtectedRoute>}/>
                        <Route path="/tests" element={<ProtectedRoute><Tests/></ProtectedRoute>}/>
                        <Route path="/studies" element={<ProtectedRoute><Studies/></ProtectedRoute>}/>
                        <Route path="/register-study" element={<ProtectedRoute><RegisterStudy/></ProtectedRoute>}/>
                        <Route path="/doctors" element={<ProtectedRoute><Doctors/></ProtectedRoute>}/>
                        <Route path="/staff" element={<ProtectedRoute><StaffList/></ProtectedRoute>}/>
                        <Route path="/doctor_messenger" element={<ProtectedRoute><DoctorMessenger/></ProtectedRoute>}/>
                        <Route path="/patient_messenger" element={<ProtectedRoute><PatientMessenger/></ProtectedRoute>}/>
                        <Route path="/doctor_reviews" element={<ProtectedRoute><DoctorsReviews/></ProtectedRoute>}/>
                        <Route path="/patient_reviews" element={<ProtectedRoute><PatientReviews/></ProtectedRoute>}/>
                        <Route path="/finances" element={<ProtectedRoute><Finances/></ProtectedRoute>}/>
                        <Route path="/settings" element={<ProtectedRoute><Navigate to="/settings/display" replace/></ProtectedRoute>}/>
                        <Route path="/settings/profiles" element={<ProtectedRoute><SettingsProfiles/></ProtectedRoute>}/>
                        <Route path="/settings/profiles/bulk-upload" element={<ProtectedRoute><BulkEmployeeUpload/></ProtectedRoute>}/>
                        <Route path="/settings/profiles/:id" element={<ProtectedRoute><SettingsProfileDetail/></ProtectedRoute>}/>
                        <Route path="/settings/profile" element={<ProtectedRoute><SettingsProfile/></ProtectedRoute>}/>
                        <Route path="/settings/inventory-types" element={<ProtectedRoute><SettingsInventoryTypes/></ProtectedRoute>}/>
                        <Route path="/settings/inventory-types/register" element={<ProtectedRoute><RegisterInventoryType/></ProtectedRoute>}/>
                        <Route path="/settings/commitment-types" element={<ProtectedRoute><SettingsCommitmentTypes/></ProtectedRoute>}/>
                        <Route path="/settings/commitment-types/register" element={<ProtectedRoute><RegisterCommitmentType/></ProtectedRoute>}/>
                        <Route path="/settings/display" element={<ProtectedRoute><SettingsDisplay/></ProtectedRoute>}/>
                        <Route path="/settings/formularios" element={<ProtectedRoute><SettingsFormularios/></ProtectedRoute>}/>
                        <Route path="/admin/roles" element={<SuperAdminRoute><AdminRoles/></SuperAdminRoute>}/>
                        {ClientPortalRoutes}
                        <Route path="/404" element={<ProtectedRoute><PageNotFound/></ProtectedRoute>}/>
                        <Route path="*" element={<Navigate to="/404" replace/>}/>
                    </Routes>
                </Suspense>
                <AppModuleTour/>
                <NotificationManager/>
            </div>
            {isMobile ? <BottomMenu/> : null}
        </div>
    )
}

export default AppLayout;